/*
  Eva PJAX: defensive same-origin navigation. Fetches the destination
  page, swaps #MainContent's innerHTML in place, updates title/meta/
  body classes, and manages history -- all so audio playing outside
  #MainContent (the Eva Radio widget, a direct child of <body>) survives
  normal site navigation instead of restarting on every page load.

  Defensive by design: anything unexpected -- a denied path, a fetch
  failure, a timeout, a response with no #MainContent to swap into --
  falls back to a completely normal browser navigation. This file only
  ever makes navigation FASTER when it works; it should never be the
  reason a link stops working.
*/
(function () {
  // Kill switch: flip to false to fully disable PJAX and fall back to
  // ordinary browser navigation everywhere, with zero other code changes.
  var EVA_PJAX_ENABLED = true;

  if (!EVA_PJAX_ENABLED) { return; }

  // Modern-browser features this file depends on -- if any are missing,
  // don't half-implement PJAX, just don't intercept anything.
  if (!window.fetch || !window.DOMParser || !window.history || !window.history.pushState) {
    return;
  }

  var MAIN_CONTENT_ID = 'MainContent';
  var FETCH_TIMEOUT_MS = 5000;
  // '/cart' is denied because theme.js binds the cart page form (qty
  // steppers, ajax updates) once at hard page load only; a PJAX-swapped
  // cart form would be inert. The cart drawer lives outside #MainContent
  // and is unaffected. Radio restarting on cart entry is the accepted
  // trade-off.
  var DENY_PATH_PREFIXES = ['/checkout', '/cart', '/account', '/password', '/cdn/'];
  var FILE_EXTENSION_RE = /\.[a-z0-9]{2,5}$/i; // e.g. /files/manual.pdf, /assets/x.zip
  var META_SELECTORS = ['description', 'og:title', 'og:description', 'og:url', 'og:image', 'twitter:title', 'twitter:description'];

  var scrollPositions = {};
  var progressBar = null;
  var progressHideTimer = null;
  var activeController = null;

  // --- Progress bar (thin, VHS-green, top of viewport) -------------------

  function ensureProgressBarStyles() {
    if (document.getElementById('EvaPjaxProgressStyles')) { return; }
    var style = document.createElement('style');
    style.id = 'EvaPjaxProgressStyles';
    style.textContent =
      '.eva-pjax-progress{position:fixed;top:0;left:0;height:3px;width:0;' +
      'background:#86c232;z-index:9999;transition:width 0.3s ease,opacity 0.2s ease;' +
      'opacity:0;pointer-events:none;}' +
      '.eva-pjax-progress--active{opacity:1;}';
    document.head.appendChild(style);
  }

  function showProgressBar() {
    ensureProgressBarStyles();
    if (progressHideTimer) {
      clearTimeout(progressHideTimer);
      progressHideTimer = null;
    }
    if (!progressBar) {
      progressBar = document.createElement('div');
      progressBar.className = 'eva-pjax-progress';
      document.body.appendChild(progressBar);
    }
    progressBar.style.width = '0';
    progressBar.classList.add('eva-pjax-progress--active');
    void progressBar.offsetWidth; // force reflow so the width transition below actually animates
    progressBar.style.width = '75%';
  }

  function hideProgressBar() {
    if (!progressBar) { return; }
    progressBar.style.width = '100%';
    progressHideTimer = setTimeout(function () {
      progressBar.classList.remove('eva-pjax-progress--active');
      progressBar.style.width = '0';
    }, 200);
  }

  // --- Link qualification -------------------------------------------------

  function isDeniedPath(pathname) {
    for (var i = 0; i < DENY_PATH_PREFIXES.length; i++) {
      if (pathname.indexOf(DENY_PATH_PREFIXES[i]) === 0) { return true; }
    }
    return FILE_EXTENSION_RE.test(pathname);
  }

  function findQualifyingLink(e) {
    if (e.defaultPrevented) { return null; }
    if (e.button !== 0) { return null; } // left-click only
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) { return null; } // let modified clicks open new tabs etc normally

    var link = e.target.closest && e.target.closest('a[href]');
    if (!link) { return null; }
    if (link.target && link.target !== '_self') { return null; }
    if (link.hasAttribute('download')) { return null; }
    if (link.closest('[data-no-pjax]')) { return null; }

    var url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (err) {
      return null;
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') { return null; }
    if (url.origin !== window.location.origin) { return null; }
    if (isDeniedPath(url.pathname)) { return null; }

    // Pure hash change on the SAME page -- let the browser's native
    // anchor-scroll behavior handle it, nothing to fetch.
    if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) {
      return null;
    }

    return url;
  }

  // --- Swap ----------------------------------------------------------

  function updateMeta(doc) {
    META_SELECTORS.forEach(function (name) {
      var selector = 'meta[name="' + name + '"], meta[property="' + name + '"]';
      var newTag = doc.querySelector(selector);
      var curTag = document.querySelector(selector);
      if (newTag && curTag) {
        curTag.setAttribute('content', newTag.getAttribute('content') || '');
      }
    });
  }

  function updateBodyClasses(doc) {
    // 'loaded' (see layout/theme.liquid's page-transition script) is
    // applied client-side ~25ms after a HARD load and never re-fires on
    // a PJAX swap -- preserve it across the swap rather than letting a
    // fetched page's static (pre-JS) class list strip it and silently
    // reintroduce a transition-hidden state.
    var hadLoaded = document.body.classList.contains('loaded');
    document.body.className = doc.body.className;
    if (hadLoaded && !document.body.classList.contains('loaded')) {
      document.body.classList.add('loaded');
    }
  }

  // theme.js's Slate-derived section framework (window.theme.sections)
  // only ever initializes a section (Flickity product slideshow,
  // PhotoSwipe zoom, variant pickers, etc.) once, at the original hard
  // page load or via the theme EDITOR's shopify:section:load event --
  // it has no idea a PJAX swap just replaced #MainContent's actual DOM
  // nodes out from under it. Without this, every section on a
  // PJAX-loaded page is inert unenhanced markup: e.g. the product
  // gallery's thumbnail <a> tags point straight at raw CDN image URLs
  // (the no-JS fallback href) with no Flickity/PhotoSwipe click
  // interception ever wired up, so clicking one just navigates there
  // directly instead of opening the in-page zoom.
  //
  // Fix: reuse theme.sections' own loadSubSections() (the exact method
  // the theme editor calls when a section reloads) against the fresh
  // #MainContent, using the SAME already-registered constructors from
  // the original page load. First drop any instances whose container
  // is no longer in the document (the page they belonged to was just
  // replaced) -- loadSubSections()'s underlying _createInstance() has
  // no dedup for this call path, so skipping the cleanup would leak a
  // new orphaned instance on every single PJAX navigation.
  function reinitThemeSections(scope) {
    var sections = window.theme && window.theme.sections;
    if (!sections || typeof sections.loadSubSections !== 'function') { return; }
    if (Array.isArray(sections.instances)) {
      sections.instances = sections.instances.filter(function (instance) {
        if (document.contains(instance.container)) { return true; }
        if (typeof instance.onUnload === 'function') {
          try { instance.onUnload(); } catch (e) {}
        }
        return false;
      });
    }
    sections.loadSubSections(scope);
  }

  // --- Opt-in script re-execution -----------------------------------------
  // <script> tags inserted via innerHTML never execute -- a standard DOM
  // quirk, not a bug in this file, but it silently breaks anything that
  // depends on inline <script> markup living INSIDE a swapped section/
  // block (unlike the theme's own <script src> tags in layout/theme.liquid,
  // which live outside #MainContent and are never swapped at all). Root
  // cause of the Källare room block's custom element -- and everything
  // downstream of it: the page-wide VHS cursor, the TV/OSD, the film list
  // -- silently failing to initialize on a PJAX-navigated-in visit while
  // working fine on a hard reload.
  //
  // Opt-in only (data-eva-pjax-exec), not "re-run every script on every
  // swap" -- several other inline scripts already exist elsewhere in this
  // theme (collection-video, map, other ai_gen blocks) that were never
  // designed to tolerate re-execution, and blindly firing all of them here
  // risks duplicate listeners/side effects with no way to audit each one
  // in this pass. Only a script explicitly marked opts in.
  //
  // Each opted-in script's text runs through a FRESH <script> element (the
  // only way to make a browser actually execute injected markup) at most
  // ONCE per tab session, keyed by the script's own text -- covers the
  // actual failure (the first time this script's markup ever arrives via
  // PJAX this session) without risking a SECOND execution of something
  // like a customElements.define() call, which throws if the same tag
  // name is registered twice. A later PJAX re-entry into a page carrying
  // this same script doesn't need it to run again anyway: once a custom
  // element tag name is registered, any new matching element inserted via
  // innerHTML auto-upgrades on its own, no script re-run required.
  var executedOnceScripts = (window.__evaPjaxExecutedScripts = window.__evaPjaxExecutedScripts || new Set());

  function reexecuteOptInScripts(scope) {
    var scripts = scope.querySelectorAll('script[data-eva-pjax-exec]');
    scripts.forEach(function (oldScript) {
      var text = oldScript.textContent || '';
      if (!text || executedOnceScripts.has(text)) { return; }
      executedOnceScripts.add(text);
      var fresh = document.createElement('script');
      for (var i = 0; i < oldScript.attributes.length; i++) {
        var attr = oldScript.attributes[i];
        fresh.setAttribute(attr.name, attr.value);
      }
      fresh.textContent = text;
      oldScript.parentNode.replaceChild(fresh, oldScript);
    });
  }

  function applySwap(html, url, push) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var newMain = doc.getElementById(MAIN_CONTENT_ID);
    var currentMain = document.getElementById(MAIN_CONTENT_ID);

    if (!newMain || !currentMain) {
      // Structural mismatch (a template with no #MainContent, a
      // malformed response, whatever) -- don't guess, just navigate
      // normally.
      window.location.href = url;
      return;
    }

    currentMain.innerHTML = newMain.innerHTML;
    reexecuteOptInScripts(currentMain);
    document.title = doc.title;
    updateMeta(doc);
    updateBodyClasses(doc);
    reinitThemeSections(currentMain);

    if (push) {
      window.history.pushState({ evaPjax: true }, '', url);
      window.scrollTo(0, 0);
    } else {
      var savedY = scrollPositions[url];
      window.scrollTo(0, typeof savedY === 'number' ? savedY : 0);
    }

    hideProgressBar();

    // Analytics page view on swap: deliberately NOT implemented.
    // Shopify's own page-view tracking (Shopify.analytics / the
    // Monorail trekkie payload) expects page-context data this file
    // has no reliable, documented way to reproduce client-side without
    // risking a malformed or duplicate event polluting real analytics
    // -- worse than not tracking the swap at all. Known limitation.

    document.dispatchEvent(new CustomEvent('eva:pjax:loaded', { detail: { url: url } }));
  }

  function navigate(url, push) {
    scrollPositions[window.location.href] = window.scrollY;

    if (activeController) { activeController.abort(); }
    activeController = new AbortController();
    var thisController = activeController;

    showProgressBar();

    var timeoutId = setTimeout(function () {
      thisController.abort();
    }, FETCH_TIMEOUT_MS);

    fetch(url, { signal: thisController.signal, credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) { throw new Error('eva-pjax: HTTP ' + res.status); }
        return res.text();
      })
      .then(function (html) {
        clearTimeout(timeoutId);
        applySwap(html, url, push);
      })
      .catch(function () {
        clearTimeout(timeoutId);

        // A NEWER navigate() call deliberately aborted this one (line
        // above: `if (activeController) { activeController.abort(); }`).
        // That is not a failure -- the newer request owns the outcome,
        // so this one must do nothing at all. Falling through to the
        // full-page-load fallback here would navigate to the *stale*
        // url, discarding the newer click AND destroying the audio this
        // whole file exists to preserve.
        //
        // Latent until the size filter bar (assets/eva-size-filter-bar.js)
        // made rapid successive navigations the NORMAL interaction --
        // checking 8.0 then M a few hundred ms apart, before the first
        // swap lands, is exactly what a multi-select filter invites.
        // Confirmed live 2026-08-05: two taps 400ms apart hard-reloaded
        // the page and dropped the second tap.
        //
        // The 5s timeout aborts via this same controller, but in that
        // case no newer navigate() has run, so thisController is still
        // activeController and the fallback below correctly fires.
        if (thisController !== activeController) { return; }

        // Any genuine failure or the 5s timeout -- full page navigation,
        // no partial/broken state left behind.
        window.location.href = url;
      });
  }

  // --- Wiring ----------------------------------------------------------

  document.addEventListener('click', function (e) {
    var url = findQualifyingLink(e);
    if (!url) { return; }
    e.preventDefault();
    navigate(url.href, true);
  });

  window.addEventListener('popstate', function () {
    navigate(window.location.href, false);
  });
})();
